import React, { createContext, useState, useContext } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, DialogContentText } from "@mui/material";
import {useTranslation} from "react-i18next";

// Типы для конфигурации модального окна
type ConfirmationModalOptions = {
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
};

type ConfirmationContextType = {
  confirm: (options: ConfirmationModalOptions) => Promise<boolean>;
};

// Создаем контекст
const ConfirmationContext = createContext<ConfirmationContextType | undefined>(undefined);

// Хук для использования контекста
export const useConfirmation = (): ConfirmationContextType => {
  const context = useContext(ConfirmationContext);
  if (!context) {
    throw new Error("useConfirmation must be used within a ConfirmationProvider");
  }
  return context;
};

// Провайдер, который будет управлять состоянием модального окна
export const ConfirmationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<ConfirmationModalOptions>({});
  const [resolvePromise, setResolvePromise] = useState<(value: boolean) => void>();
  const { t } =  useTranslation()
  // Открытие окна с Promise для результата
  const confirm = (options: ConfirmationModalOptions): Promise<boolean> => {
    setOptions(options);
    setIsOpen(true);

    return new Promise((resolve) => {
      setResolvePromise(() => resolve); // сохраняем resolve для обработки результата
    });
  };

  // Обработчик подтверждения
  const handleConfirm = () => {
    setIsOpen(false);
    if (resolvePromise) resolvePromise(true);
  };

  // Обработчик отмены
  const handleCancel = () => {
    setIsOpen(false);
    if (resolvePromise) resolvePromise(false);
  };

  return (
    <ConfirmationContext.Provider value={{ confirm }}>
      {children}

      {/* Глобальный компонент модального окна */}
      <Dialog open={isOpen} onClose={handleCancel}>
        <DialogTitle>{options.title || "Подтверждение"}</DialogTitle>
        <DialogContent>
          <DialogContentText>{options.message || "Вы уверены?"}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirm} color="primary" autoFocus>
            {options.confirmText || t('common.yes')}
          </Button>
          <Button onClick={handleCancel} color="secondary">
            {options.cancelText || t('common.no')}
          </Button>
        </DialogActions>
      </Dialog>
    </ConfirmationContext.Provider>
  );
};